import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/synapse/Select';
import { Field } from '@/components/synapse/Field';
import { Icon } from '@/components/synapse/Icon';
import { PaginationFooter } from '@/components/synapse/PaginationFooter';
import { InitialsAvatar } from '@/components/synapse/Avatar';
import { useAuditLogs } from '@/hooks/useAuditLog';
import { useClinicMembers } from '@/hooks/usePeople';
import { ApiError } from '@/lib/api';
import { translateApiError } from '@/lib/apiError';
import { formatRelativeTime } from '@/lib/relativeTime';
import { cn } from '@/lib/utils';
import type { AuditAction, AuditLogEntry } from '@/types/api';

const PAGE_SIZE = 25;

const ACTION_LABEL_KEY: Record<AuditAction, string> = {
  'stock.in': 'audit.actions.stockIn',
  'stock.issue': 'audit.actions.stockIssue',
  'stock.adjustment': 'audit.actions.stockAdjustment',
  'stock.movement.update': 'audit.actions.stockMovementUpdate',
  'stock.movement.delete': 'audit.actions.stockMovementDelete',
  'item.create': 'audit.actions.itemCreate',
  'batch.create': 'audit.actions.batchCreate',
  'user.create': 'audit.actions.userCreate',
  'user.update': 'audit.actions.userUpdate',
  'user.remove': 'audit.actions.userRemove',
  'invitation.create': 'audit.actions.invitationCreate',
  'invitation.revoke': 'audit.actions.invitationRevoke',
  'invitation.accept': 'audit.actions.invitationAccept',
  'apt_ticket.issue': 'audit.actions.aptTicketIssue',
  'apt_ticket.cancel': 'audit.actions.aptTicketCancel',
  'consultation.record': 'audit.actions.consultationRecord',
  'consultation.amend': 'audit.actions.consultationAmend',
};

const ACTION_VALUES: AuditAction[] = [
  'stock.in',
  'stock.issue',
  'stock.adjustment',
  'stock.movement.update',
  'stock.movement.delete',
  'item.create',
  'batch.create',
  'user.create',
  'user.update',
  'user.remove',
  'invitation.create',
  'invitation.revoke',
  'invitation.accept',
  'apt_ticket.issue',
  'apt_ticket.cancel',
  'consultation.record',
  'consultation.amend',
];

export function AuditLogPage() {
  const { t } = useTranslation();
  const [actorUserId, setActorUserId] = useState<string>('');
  const [action, setAction] = useState<'' | AuditAction>('');
  const [from, setFrom] = useState<string>('');
  const [to, setTo] = useState<string>('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [actorUserId, action, from, to]);

  const { data: members = [] } = useClinicMembers();

  const { data, isLoading, isError, error, refetch, isFetching } = useAuditLogs({
    actorUserId: actorUserId || undefined,
    action: action || undefined,
    from: from ? new Date(from).toISOString() : undefined,
    to: to ? new Date(`${to}T23:59:59.999Z`).toISOString() : undefined,
    page,
    pageSize: PAGE_SIZE,
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const filtersActive = actorUserId !== '' || action !== '' || from !== '' || to !== '';
  const noData = !isLoading && !isError && total === 0;
  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(total, page * PAGE_SIZE);

  const clearFilters = () => {
    setActorUserId('');
    setAction('');
    setFrom('');
    setTo('');
  };

  return (
    <div className="mx-auto w-full max-w-[1180px]">
      <header className="mb-5 flex flex-col gap-1.5 sm:mb-6">
        <h1 className="text-2xl font-semibold tracking-[-0.02em] sm:text-[26px]">
          {t('audit.title')}
        </h1>
        <p className="text-[13.5px] text-muted">{t('audit.subtitle')}</p>
      </header>

      {/* Every filter is labelled on the page itself, not only to screen
          readers. Two adjacent empty date inputs both read "dd/mm/yyyy", so
          without visible labels there is nothing on screen that says which one
          is the start of the range — least of all on mobile, where they stack
          and lose even their left-to-right ordering as a hint. */}
      <div className="mb-4 grid gap-3 sm:mb-5 sm:grid-cols-2 lg:grid-cols-4">
        <Field label={t('audit.filterByActor')}>
          <Select value={actorUserId} onChange={(e) => setActorUserId(e.target.value)}>
            <option value="">{t('audit.allPeople')}</option>
            {members.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name} ({m.email})
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('audit.filterByAction')}>
          <Select value={action} onChange={(e) => setAction(e.target.value as '' | AuditAction)}>
            <option value="">{t('audit.allActions')}</option>
            {ACTION_VALUES.map((v) => (
              <option key={v} value={v}>
                {t(ACTION_LABEL_KEY[v])}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('audit.fromDate')}>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </Field>
        <Field label={t('audit.toDate')}>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </Field>
      </div>

      {isLoading ? (
        <LoadingState />
      ) : isError ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : noData && !filtersActive ? (
        <EmptyState />
      ) : noData ? (
        <FilteredEmptyState onClear={clearFilters} />
      ) : (
        <Card className={cn('overflow-hidden p-0', isFetching && 'opacity-95')}>
          <ul className="divide-y divide-line">
            {items.map((entry) => (
              <li key={entry.id}>
                <Row entry={entry} />
              </li>
            ))}
          </ul>
          <PaginationFooter
            page={page}
            totalPages={totalPages}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            total={total}
            onPrev={() => setPage((p) => Math.max(1, p - 1))}
            onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
          />
        </Card>
      )}
    </div>
  );
}

function Row({ entry }: { entry: AuditLogEntry }) {
  const { t } = useTranslation();
  const when = useMemo(
    () => formatRelativeTime(new Date(entry.createdAt), t),
    [entry.createdAt, t],
  );
  const summary = describeEntry(entry, t);
  return (
    <div className="flex items-start gap-3 px-4 py-3">
      <InitialsAvatar name={entry.actorName} size={32} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="text-[13.5px] font-semibold tracking-[-0.01em]">
            {entry.actorName}
          </span>
          <span className="text-[12.5px] text-muted">
            {ACTION_LABEL_KEY[entry.action] ? t(ACTION_LABEL_KEY[entry.action]) : entry.action}
          </span>
        </div>
        {summary && (
          <div className="mt-0.5 text-[12.5px] text-ink-2 break-words">{summary}</div>
        )}
        <div className="mt-1 text-[11.5px] text-muted">
          <span title={new Date(entry.createdAt).toLocaleString()}>{when}</span>
          <span className="mx-1.5 text-muted-2">·</span>
          <span className="text-muted-2">{entry.actorEmail}</span>
        </div>
      </div>
    </div>
  );
}

function describeEntry(entry: AuditLogEntry, t: (key: string, opts?: Record<string, unknown>) => string): string | null {
  const m = entry.metadata as Record<string, unknown>;
  const brand = typeof m.brandName === 'string' ? (m.brandName as string) : null;
  const generic = typeof m.genericName === 'string' ? (m.genericName as string) : null;
  const email = typeof m.email === 'string' ? (m.email as string) : null;
  const quantity = typeof m.quantity === 'number' ? (m.quantity as number) : null;
  const reason = typeof m.reason === 'string' ? (m.reason as string) : null;
  const previousRole = typeof m.previousRole === 'string' ? (m.previousRole as string) : null;
  const newRole = typeof m.newRole === 'string' ? (m.newRole as string) : null;
  const itemBrand = typeof m.itemBrandName === 'string' ? (m.itemBrandName as string) : null;
  const itemFallback = t('audit.item');
  const userFallback = t('audit.user');

  switch (entry.action) {
    case 'stock.in':
      return `+${quantity ?? '?'} of ${itemBrand ?? itemFallback}`;
    case 'stock.issue':
      return `-${quantity ?? '?'} of ${itemBrand ?? itemFallback}`;
    case 'stock.adjustment':
      return `${quantity != null && quantity > 0 ? '+' : ''}${quantity ?? '?'}${reason ? ` (${reason})` : ''}`;
    case 'item.create':
      return brand ? `${brand}${generic ? ` · ${generic}` : ''}` : null;
    case 'batch.create': {
      const bn = typeof m.batchNumber === 'string' ? (m.batchNumber as string) : null;
      return itemBrand ? (bn ? t('audit.summaryBatchCreated', { brand: itemBrand, batchNumber: bn }) : itemBrand) : null;
    }
    case 'user.create':
      return email;
    case 'user.update':
      if (previousRole && newRole) return `${email ?? userFallback}: ${previousRole} → ${newRole}`;
      return email;
    case 'user.remove':
      return email;
    // The roles are the substance of an invitation — who was offered what —
    // so the summary line carries them rather than the address alone.
    case 'invitation.create':
    case 'invitation.revoke':
    case 'invitation.accept': {
      const roles = Array.isArray(m.roles)
        ? (m.roles as unknown[]).filter((r): r is string => typeof r === 'string')
        : [];
      if (!email) return roles.join(', ') || null;
      return roles.length > 0 ? `${email} · ${roles.join(', ')}` : email;
    }
    // The number is the whole event; the patient is deliberately not named
    // here — the audit log is read by people who do not hold `patients.read`.
    case 'apt_ticket.issue':
    case 'apt_ticket.cancel': {
      const number = typeof m.number === 'number' ? m.number : null;
      return number != null ? t('aptCounter.numberLabel', { number }) : null;
    }
    default:
      return null;
  }
}

function LoadingState() {
  return (
    <Card className="overflow-hidden p-0" aria-busy="true" aria-live="polite">
      <ul className="divide-y divide-line">
        {[0, 1, 2, 3, 4].map((i) => (
          <li key={i} className="h-[68px] animate-pulse bg-paper-2" />
        ))}
      </ul>
    </Card>
  );
}

function EmptyState() {
  const { t } = useTranslation();
  return (
    <Card className="flex flex-col items-center justify-center gap-3 px-6 py-12 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-paper-2 text-muted">
        <Icon name="history" size={22} />
      </div>
      <div className="text-[14px] font-semibold tracking-[-0.01em]">{t('audit.noActivityYet')}</div>
      <p className="max-w-[320px] text-[12.5px] text-muted">{t('audit.noActivityBody')}</p>
    </Card>
  );
}

function FilteredEmptyState({ onClear }: { onClear: () => void }) {
  const { t } = useTranslation();
  return (
    <Card className="flex flex-col items-center justify-center gap-3 px-6 py-12 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-paper-2 text-muted">
        <Icon name="search" size={22} />
      </div>
      <div className="text-[14px] font-semibold tracking-[-0.01em]">{t('audit.noEventsMatch')}</div>
      <p className="max-w-[320px] text-[12.5px] text-muted">{t('audit.tryWiderRange')}</p>
      <Button variant="ghost" onClick={onClear}>
        {t('audit.clearFilters')}
      </Button>
    </Card>
  );
}

function ErrorState({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const { t } = useTranslation();
  const message = error instanceof ApiError ? translateApiError(error) : t('audit.couldNotLoadMsg');
  return (
    <Card className="flex flex-col items-center justify-center gap-3 px-6 py-10 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive-bg text-destructive">
        <Icon name="alert" size={22} />
      </div>
      <div className="text-[14px] font-semibold tracking-[-0.01em]">{t('audit.couldNotLoad')}</div>
      <p className="max-w-[320px] text-[12.5px] text-muted">{message}</p>
      <Button onClick={onRetry}>{t('common.tryAgain')}</Button>
    </Card>
  );
}

